import React, { useContext, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Tab, Tabs, Spinner, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import { TranslatorContext } from "../../../context/Translator";
import CustomerInfoTab from "./tabs/CustomerInfoTab";
import AppointmentsTab from "./tabs/AppointmentsTab";
import PetsTab from "./tabs/PetsTab";
import SalesTab from './tabs/SalesTab';

export default function CustomerProfilePage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const [customer, setCustomer] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [pets, setPets] = useState([]);
    const [breeds, setBreeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [loadingPets, setLoadingPets] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState("customer");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [sales, setSales] = useState([]);
    const [loadingSales, setLoadingSales] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);

    useEffect(() => {
        fetchCustomerDetails();
    }, [id]);

    useEffect(() => {
        if (activeTab === "appointments") {
            fetchCustomerAppointments();
        } else if (activeTab === "pets") {
            fetchCustomerPets();
        } else if (activeTab === "sales") {
            fetchSales(id);
            setSelectedCustomerId(id);
        }
    }, [activeTab, id, refreshTrigger]);

    const fetchCustomerDetails = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-profile/${id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.customer) {
                setCustomer(response.data.customer);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching customer:", err);
            setError("Failed to load customer details");
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-appointments/${id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setAppointments(response.data.appointments);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching appointments:", err);
            setError("Failed to load appointments");
        } finally {
            setLoadingAppointments(false);
        }
    };

    const fetchCustomerPets = async () => {
        try {
            setLoadingPets(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-pets/${id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setPets(response.data.pets);
                setBreeds(response.data.breeds || []);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching pets:", err);
            setError("Failed to load pets");
        } finally {
            setLoadingPets(false);
        }
    };

    const fetchSales = async (customerId) => {
        setLoadingSales(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-sales/${customerId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data.status) {
                setSales(response.data.sales || []);
            }
        } catch (error) {
            console.error("Error fetching sales:", error);
        } finally {
            setLoadingSales(false);
        }
    };

    // ADD THIS FUNCTION - Refresh sales data
    const refreshSalesData = async () => {
        if (selectedCustomerId) {
            await fetchSales(selectedCustomerId);
        }
    };

    const refreshPets = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const showSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 3000);
    };

    const showError = (message) => {
        setError(message);
        setTimeout(() => setError(null), 3000);
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">{t('loading')}</p>
                </div>
            </PageLayout>
        );
    }

    if (error && !customer) {
        return (
            <PageLayout>
                <Alert variant="danger" className="m-4">
                    {error}
                </Alert>
            </PageLayout>
        );
    }

    if (!customer) {
        return (
            <PageLayout>
                <Alert variant="warning" className="m-4">
                    Customer not found
                </Alert>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">{t('customer profile')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to='#' className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to='/customers' className="mc-breadcrumb-link">{t('customers')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('customer profile')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {success && (
                    <Col xl={12}>
                        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
                            {success}
                        </Alert>
                    </Col>
                )}

                {error && (
                    <Col xl={12}>
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    </Col>
                )}

                <Col xl={12}>
                    <div className="mc-card p-4">
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            id="customer-tabs"
                            className="mc-tabs"
                            mountOnEnter={true}
                            unmountOnExit={false}
                            style={{ gap: '10px' }}
                        >
                            {/* Customer Information Tab */}
                            <Tab eventKey="customer" title={t('customer information')} className="mc-tabpane">
                                <CustomerInfoTab
                                    customer={customer}
                                    customerId={id}
                                    onCustomerUpdate={fetchCustomerDetails}
                                    onShowSuccess={showSuccess}
                                    onShowError={showError}
                                />
                            </Tab>

                            {/* Appointments Tab */}
                            <Tab eventKey="appointments" title={t('appointments')} className="mc-tabpane">
                                <AppointmentsTab
                                    appointments={appointments}
                                    loading={loadingAppointments}
                                    customerId={id}
                                />
                            </Tab>

                            {/* Sales Tab - ADDED onRefresh prop */}
                            <Tab eventKey="sales" title={t('sales')} className="mc-tabpane">
                                <SalesTab
                                    sales={sales}
                                    loading={loadingSales}
                                    customerId={selectedCustomerId}
                                    onRefresh={refreshSalesData}  // <-- ADD THIS LINE
                                />
                            </Tab>

                            {/* Pets Tab */}
                            <Tab eventKey="pets" title={t('pets')} className="mc-tabpane">
                                <PetsTab
                                    pets={pets}
                                    breeds={breeds}
                                    customerId={id}
                                    loading={loadingPets}
                                    onPetsUpdate={refreshPets}
                                    onShowSuccess={showSuccess}
                                    onShowError={showError}
                                />
                            </Tab>
                        </Tabs>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}